/**
 * KPI card. Variante "highlight" pra tonar valores-chave com borda turquesa.
 */
import type { ReactNode } from "react";
import { Info } from "lucide-react";

interface Props {
  label: string;
  value: string;
  sub?: string;
  subTone?: "neutral" | "positive" | "negative" | "muted";
  icon?: ReactNode;
  highlight?: boolean;
  /** Cor do VALOR principal. "negative" → coral (ex.: menor saldo abaixo do
   * piso de caixa). Default neutro (branco; turquesa quando highlight). */
  valueTone?: "neutral" | "negative";
  /** Nuance que NÃO cabe no subtítulo — vira ícone (i) com title ao lado do
   * label (ex.: #244 "fornecimento ≠ pagamento" do Varejo). */
  tooltip?: string;
  /** data-testid no card; quando presente, value/sub ganham `-value`/`-sub`. */
  testId?: string;
}

export function KpiCard({ label, value, sub, subTone = "neutral", icon, highlight = false, valueTone = "neutral", tooltip, testId }: Props) {
  const borderCls =
    valueTone === "negative"
      ? "border-indra-negativo/40 bg-indra-azul-surface"
      : highlight
        ? "border-indra-turquesa/40 bg-indra-turquesa-muted"
        : "border-white/10 bg-indra-azul-surface";
  const valueCls =
    valueTone === "negative"
      ? "text-indra-negativo"
      : highlight
        ? "text-indra-turquesa"
        : "text-white";

  const subToneCls =
    subTone === "positive"
      ? "text-indra-positivo"
      : subTone === "negative"
        ? "text-indra-negativo"
        : subTone === "muted"
          ? "text-white/60" // a11y: /40 informativo nao passa AA (UX 03/07 item 8a)
          : "text-white/60";

  return (
    <div
      className={`rounded-xl border ${borderCls} p-5 flex flex-col gap-2 h-full`}
      data-testid={testId}
    >
      <div className="flex items-center justify-between">
        <span
          className={
            // sentence case (UX 03/07 item 6): sem uppercase forcado no label
            "text-[11px] tracking-wider text-white/60" +
            (tooltip ? " flex items-center gap-1.5" : "")
          }
        >
          {label}
          {tooltip && (
            <span
              className="cursor-help"
              title={tooltip}
              data-testid={testId ? `${testId}-tooltip` : undefined}
            >
              <Info size={11} className="text-white/40" />
            </span>
          )}
        </span>
        {icon && <span className="text-white/40">{icon}</span>}
      </div>
      {/* nowrap+tabular: o hifen do fmtBRL e quebravel e o "-" orfava
          numa linha sozinho (UX 03/07 item 1). */}
      <div
        className={`text-2xl font-black ${valueCls} font-sans whitespace-nowrap tabular-nums`}
        data-testid={testId ? `${testId}-value` : undefined}
      >
        {value}
      </div>
      {sub && (
        <div
          className={`text-xs ${subToneCls}`}
          data-testid={testId ? `${testId}-sub` : undefined}
        >
          {sub}
        </div>
      )}
    </div>
  );
}
